import { useTenantChecksum, useUser } from "../../hooks/authHooks";
import Menu from "@jnctn-app/core/components/Menu.component";
import MenuItem from "@jnctn-app/core/components/MenuItem.component";
import { ReactComponent as KeyboardIcon } from "@jnctn/icons/keyboard.svg";
import { ReactComponent as QRCodeScanIcon } from "@jnctn/icons/qr-code-scan.svg";
import { ReactComponent as LeftIcon } from "@jnctn/icons/left.svg";
import { ReactComponent as CredentialsIcon } from "@jnctn/icons/credentials.svg";
import { ReactComponent as MeIcon } from "@jnctn/icons/me.svg";
import { ReactComponent as MoreIcon } from "@jnctn/icons/more.svg";
import { ReactComponent as CredentialsActiveIcon } from "@jnctn/icons/credentials-active.svg";
import { ReactComponent as MeActiveIcon } from "@jnctn/icons/me-active.svg";
import { ReactComponent as MoreActiveIcon } from "@jnctn/icons/more-active.svg";
import isCameraSupported from "@jnctn/webcore/ui-helpers/isCameraSupported";
import FlexLayoutItem from "@jnctn/design-system/Flexbox/FlexLayoutItem.component";
import FullPageLayout, {
  Type,
} from "@jnctn/design-system/Layout/FullPageLayout.component";
import {
  ComponentProps,
  lazy,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import cx from "classnames";
import Loader from "@jnctn/design-system/Loader/Loader.component";
import InstallableNotice from "@jnctn-app/core/notices/InstallableNotice";
import Notices from "@jnctn-app/core/notices/Notices";
import OfflineNotice from "@jnctn-app/core/notices/OfflineNotice";
import TransientNotices from "@jnctn-app/core/notices/TransientNotices";
import UpdateNotice from "@jnctn-app/core/notices/UpdateNotice";
import { motion } from "motion/react";
import match from "@jnctn/fp/match";
import Button from "@jnctn/design-system/Button/Button.component";
import BackContext from "@jnctn-app/contexts/BackContext";
import { useNavigate } from "react-router";
import AnimatePresenceWrapper from "@jnctn/design-system/AnimatePresenceWrapper/AnimatePresenceWrapper.component";

function AuthenticatedMenu({
  color = "white",
  shadow = true,
}: Pick<ComponentProps<typeof Menu>, "color" | "shadow">) {
  const tenantChecksum = useTenantChecksum();

  if (tenantChecksum === undefined) {
    return null;
  }

  return (
    <Menu color={color} shadow={shadow}>
      <MenuItem
        label="me"
        to="/"
        icon={<MeIcon />}
        iconActive={<MeActiveIcon />}
      />
      <MenuItem
        label="credentials"
        to={`/${tenantChecksum.checksum}/credentials/`}
        icon={<CredentialsIcon />}
        iconActive={<CredentialsActiveIcon />}
      />
      {isCameraSupported() ? (
        <MenuItem
          label="scan code"
          alwaysActive={true}
          to="/scan/"
          iconWrapperClassName="p-1 rounded-full bg-gradient-to-br from-lime-400 to-ocean-500 -m-1"
          icon={
            <QRCodeScanIcon
              className={cx("rounded-full w-8 h-8", {
                "bg-white": color === "white",
                "bg-charcoal-800": color === "black",
              })}
            />
          }
        />
      ) : (
        <MenuItem icon={<KeyboardIcon />} label="code" to="/code/" />
      )}
      <MenuItem
        label="more"
        to="/more/"
        icon={<MoreIcon />}
        iconActive={<MoreActiveIcon />}
      />
    </Menu>
  );
}

const loaderColor = match<Type, ComponentProps<typeof Loader>["color"]>(
  [["black", "gradient"], "white"],
  [["gray", "white"], "black"]
);

const backColor = match<Type, "text-white" | "text-black">(
  [["black", "gradient"], "text-white"],
  [["gray", "white"], "text-black"]
);

type Props = {
  children: ReactNode;
  type?: Type;
  menuColor?: "black" | "white";
  hasMenu?: boolean;
  className?: string;
  hasMenuShadow?: boolean;
};

export default function StandardPageLayout({
  children,
  type = "black",
  hasMenu = true,
  menuColor = "white",
  className,
  hasMenuShadow = true,
}: Props) {
  const user = useUser();
  const { back } = useContext(BackContext);
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    const { action } = back;
    switch (typeof action) {
      case "function":
        action();
        break;
      case "string":
        navigate(action);
        break;
    }
  }, [navigate, back]);
  // We want to hide optimistically before the back value propagates through the
  // context value
  const [optimisticHidden, setOptimisticHidden] = useState(back !== undefined);
  useEffect(() => {
    setOptimisticHidden(false);
  }, [back]);
  const onOptimisticBack = useCallback(() => {
    setOptimisticHidden(true);
    onBack();
  }, [onBack]);
  return (
    <>
      <Notices hasMenu={hasMenu}>
        <AnimatePresenceWrapper>
          <OfflineNotice key="offline" />
          <UpdateNotice key="update" />
          <InstallableNotice key="installable" />
          <TransientNotices key="transient" />
        </AnimatePresenceWrapper>
      </Notices>
      <FullPageLayout
        direction="vertical"
        element="section"
        align="stretch"
        type={type}
        className={className}
      >
        <Suspense
          fallback={
            <FlexLayoutItem grow={1}>
              <Loader color={loaderColor(type)} />
            </FlexLayoutItem>
          }
        >
          {children}
        </Suspense>
        {hasMenu && user !== undefined ? (
          <Suspense fallback={null}>
            <AuthenticatedMenu color={menuColor} shadow={hasMenuShadow} />
          </Suspense>
        ) : null}
        {!optimisticHidden && back.action !== undefined ? (
          <Button
            labelHidden={true}
            label="Back"
            icon={<LeftIcon className="w-10 h-10" />}
            onClick={onOptimisticBack}
            className={cx(
              "absolute top-6 left-3 z-50",
              backColor(back.color || type)
            )}
            element={
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              />
            }
          />
        ) : null}
      </FullPageLayout>
    </>
  );
}
